import type { Camera } from './Camera';
import { canvasHeight, canvasWidth, themeMarbleDark } from './gameConstants';
import { initialZoom } from './constantsCamera';
import type { StageDef } from './data/maps';
import type { Marble } from './Marble';
import type { ColorTheme } from './types/ColorTheme';
import type { MapEntityState } from './types/MapEntity.type';
import type { VectorLike } from './types/VectorLike';

export type MarbleRenderParameters = {
  camera: Camera;
  stage: StageDef;
  entities: MapEntityState[];
  marbles: Marble[];
  winners: Marble[];
  winnerRank: number;
  winner: Marble | null;
  size: VectorLike;
  theme: ColorTheme;
};

export class MarbleRouletteRenderer {
  private readonly canvas: HTMLCanvasElement;
  private ctx!: CanvasRenderingContext2D;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
  }

  init(): void {
    this.canvas.width = canvasWidth;
    this.canvas.height = canvasHeight;
    const ctx = this.canvas.getContext('2d', { alpha: false });
    if (!ctx) throw new Error('Canvas 2D unavailable');
    this.ctx = ctx;
  }

  get width(): number {
    return this.canvas.width;
  }

  get height(): number {
    return this.canvas.height;
  }

  render(renderParameters: MarbleRenderParameters): void {
    const theme = renderParameters.theme ?? themeMarbleDark;
    this.ctx.fillStyle = theme.background;
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

    this.ctx.save();
    this.ctx.scale(initialZoom, initialZoom);
    this.ctx.textAlign = 'left';
    this.ctx.textBaseline = 'top';
    this.ctx.font = '0.4pt sans-serif';
    this.ctx.lineWidth = 3 / (renderParameters.camera.zoom + initialZoom);

    renderParameters.camera.renderScene(this.ctx, () => {
      this.renderEntities(renderParameters.entities, theme);
      this.renderMarbles(renderParameters, theme);
    });

    this.ctx.restore();

    this.renderWinner(renderParameters, theme);
  }

  private renderEntities(entities: MapEntityState[], theme: ColorTheme): void {
    this.ctx.save();
    entities.forEach(entity => {
      const transform = this.ctx.getTransform();
      this.ctx.translate(entity.x, entity.y);
      this.ctx.rotate(entity.angle);
      this.ctx.fillStyle = entity.shape.color ?? theme.entity[entity.shape.type].fill;
      this.ctx.strokeStyle = entity.shape.color ?? theme.entity[entity.shape.type].outline;
      this.ctx.shadowBlur = theme.entity[entity.shape.type].bloomRadius;
      this.ctx.shadowColor =
        entity.shape.bloomColor ?? entity.shape.color ?? theme.entity[entity.shape.type].bloom;

      const shape = entity.shape;
      switch (shape.type) {
        case 'polyline':
          if (shape.points.length > 0) {
            this.ctx.beginPath();
            this.ctx.moveTo(shape.points[0][0], shape.points[0][1]);
            for (let i = 1; i < shape.points.length; i++) {
              this.ctx.lineTo(shape.points[i][0], shape.points[i][1]);
            }
            this.ctx.stroke();
          }
          break;
        case 'box': {
          const w = shape.width * 2;
          const h = shape.height * 2;
          this.ctx.rotate(shape.rotation);
          this.ctx.fillRect(-w / 2, -h / 2, w, h);
          this.ctx.strokeRect(-w / 2, -h / 2, w, h);
          break;
        }
        case 'circle':
          this.ctx.beginPath();
          this.ctx.arc(0, 0, shape.radius, 0, Math.PI * 2, false);
          this.ctx.stroke();
          break;
      }

      this.ctx.setTransform(transform);
    });
    this.ctx.restore();
  }

  private renderMarbles(renderParameters: MarbleRenderParameters, theme: ColorTheme): void {
    const { marbles, camera, winnerRank, winners, size } = renderParameters;
    const winnerIndex = winnerRank - winners.length;

    const viewPort = {
      x: camera.x,
      y: camera.y,
      w: size.x,
      h: size.y,
      zoom: camera.zoom * initialZoom,
    };

    marbles.forEach((marble, i) => {
      marble.render(this.ctx, camera.zoom * initialZoom, i === winnerIndex, false, undefined, viewPort, theme);
    });
  }

  private renderWinner(renderParameters: MarbleRenderParameters, theme: ColorTheme): void {
    const { winner } = renderParameters;
    if (!winner) return;

    this.ctx.save();
    this.ctx.fillStyle = theme.winnerBackground;
    this.ctx.fillRect(this.canvas.width / 2, this.canvas.height - 168, this.canvas.width / 2, 168);

    const marbleSize = 100;
    const marbleCenterX = this.canvas.width - marbleSize / 2 - 20;
    const marbleCenterY = this.canvas.height - 168 / 2;

    this.ctx.beginPath();
    this.ctx.arc(marbleCenterX, marbleCenterY, marbleSize / 2, 0, Math.PI * 2);
    this.ctx.fillStyle = `hsl(${winner.hue} 100% ${theme.marbleLightness}%)`;
    this.ctx.fill();

    this.ctx.fillStyle = theme.winnerText;
    this.ctx.strokeStyle = theme.winnerOutline;

    this.ctx.font = 'bold 48px sans-serif';
    this.ctx.textAlign = 'right';
    this.ctx.lineWidth = 4;
    const textRightX = marbleCenterX - marbleSize / 2 - 20;
    if (theme.winnerOutline) {
      this.ctx.strokeText('Winner', textRightX, this.canvas.height - 120);
    }
    this.ctx.fillText('Winner', textRightX, this.canvas.height - 120);

    this.ctx.font = 'bold 72px sans-serif';
    this.ctx.fillStyle = `hsl(${winner.hue} 100% ${theme.marbleLightness}%)`;
    if (theme.winnerOutline) {
      this.ctx.strokeText(winner.name, textRightX, this.canvas.height - 55);
    }
    this.ctx.fillText(winner.name, textRightX, this.canvas.height - 55);
    this.ctx.restore();
  }
}
